/**
 * Gameplay effects of enchantments (vanilla 1.21+ formulas). Pure functions so
 * the smoke tests and main.ts share them.
 */

import { I } from '../itemIds';
import type { DamageKind, Stack } from '../types';
import { enchLevel } from './stack';
import { ARTHROPOD, E, UNDEAD } from './registry';
import * as B from '../../blocks';
import { cropOf, isMatureCrop } from '../farming';

/** Extra melee damage from Sharpness / Smite / Bane of Arthropods vs `mobType`. */
export function enchantDamageBonus(weapon: Stack | null, mobType: string, edition: 'bedrock' | 'java' = 'java'): number {
  if (!weapon) return 0;
  let bonus = 0;
  const sharp = enchLevel(weapon, E.SHARPNESS);
  if (sharp > 0) bonus += edition === 'bedrock' ? 1.25 * sharp : 0.5 * sharp + 0.5;
  const smite = enchLevel(weapon, E.SMITE);
  if (smite > 0 && UNDEAD.has(mobType)) bonus += 2.5 * smite;
  const bane = enchLevel(weapon, E.BANE_OF_ARTHROPODS);
  if (bane > 0 && ARTHROPOD.has(mobType)) bonus += 2.5 * bane;
  return bonus;
}

/** Sweep damage dealt to each nearby mob: 1 + attack * (SE level / (SE level + 1)). */
export function sweepDamage(attackDamage: number, sweepingLevel: number): number {
  return 1 + (sweepingLevel > 0 ? attackDamage * (sweepingLevel / (sweepingLevel + 1)) : 0);
}

/** EPF of a full armor set against a damage kind (vanilla, before the 20 cap). */
export function protectionEPF(armor: readonly Stack[], kind: DamageKind): number {
  let epf = 0;
  for (const s of armor) {
    if (!s || s.count <= 0) continue;
    epf += enchLevel(s, E.PROTECTION);
    if (kind === 'fire' || kind === 'lava') epf += 2 * enchLevel(s, E.FIRE_PROTECTION);
    if (kind === 'explosion') epf += 2 * enchLevel(s, E.BLAST_PROTECTION);
    if (kind === 'projectile') epf += 2 * enchLevel(s, E.PROJECTILE_PROTECTION);
    if (kind === 'fall') epf += 3 * enchLevel(s, E.FEATHER_FALLING);
  }
  return epf;
}

/** Unbreaking: does this use actually cost durability? Armor: 60% + 40%/(lvl+1); others 1/(lvl+1). */
export function consumesDurability(s: Stack, isArmor: boolean, rnd: () => number): boolean {
  const lvl = enchLevel(s, E.UNBREAKING);
  if (lvl <= 0) return true;
  if (isArmor) return rnd() < 0.6 + 0.4 / (lvl + 1);
  return rnd() < 1 / (lvl + 1);
}
/** Durability actually lost out of `amount` uses. */
export function durabilityLoss(s: Stack, amount: number, isArmor: boolean, rnd: () => number): number {
  let n = 0;
  for (let i = 0; i < amount; i++) if (consumesDurability(s, isArmor, rnd)) n++;
  return n;
}

/** Efficiency: added to the mining speed of a tool that is fast on the block (lvl^2 + 1). */
export const efficiencyBonus = (lvl: number): number => (lvl > 0 ? lvl * lvl + 1 : 0);

/** Fire Aspect: ticks of fire on the target (4 s per level). */
export const fireAspectTicks = (lvl: number): number => lvl * 80;

/** Bow arrow base damage with Power: 2 + 0.5*(lvl+1) (i.e. +25% * (lvl+1)). */
export const powerArrowDamage = (lvl: number): number => 2 + (lvl > 0 ? 0.5 * lvl + 0.5 : 0);

// --- block drops: Silk Touch & Fortune ------------------------------------------------

const name = (id: number): string => B.BLOCKS[id]?.name ?? '';
/** Ores whose drop count vanilla multiplies with `ore_drops` Fortune. */
function isOreDrop(blockId: number): boolean {
  return /Ore$/.test(name(blockId)) && !/Redstone|Lapis|Copper|Nether Gold/.test(name(blockId));
}
/** Blocks Silk Touch returns as themselves (things that otherwise drop something else or nothing). */
export function silkTouchDrop(blockId: number, normalItem: number): number {
  const n = name(blockId);
  if (!n || blockId === B.AIR) return 0;
  if (/Ore$|Glass|Ice$|Leaves$|^Grass Block$|^Mycelium$|^Podzol$|^Stone$|^Deepslate$|Bookshelf|Glowstone|Clay|Melon|Mushroom Block|^Snow Block$|Cobweb|Sea Lantern|Gravel/.test(n)) return blockId;
  return normalItem;
}

/**
 * Apply Fortune to normal (non-silk) drops of `blockId`.
 * Ores: vanilla ore_drops — with probability 2/(lvl+2) no bonus, else x(2..lvl+1).
 * Redstone/lapis/copper/nether gold: uniform +0..lvl per drop (vanilla uniform_bonus_count).
 * Gravel flint chance 10% / 14% / 25% / 100%.
 */
export function applyFortune(blockId: number, drops: Stack[], lvl: number, rnd: () => number): Stack[] {
  if (lvl <= 0) return drops;
  const n = name(blockId);
  if (blockId === B.GRAVEL_ID) {
    const chance = [0.1, 0.14285715, 0.25, 1][Math.min(3, lvl)];
    return [{ id: rnd() < chance ? I.FLINT : B.GRAVEL_ID, count: 1, dmg: 0 }];
  }
  if (/^(Short Grass|Fern)$/.test(n)) {
    // grass loot: uniform_bonus_count, bonusMultiplier 2 -> + rand(0..2*level) seeds
    for (const s of drops) if (s.id === I.WHEAT_SEEDS) s.count += Math.floor(rnd() * (2 * lvl + 1));
    return drops;
  }
  if (isMatureCrop(blockId)) {
    // crops: apply_bonus binomial — `lvl` more trials at 0.5714 on the bonus item
    const k = cropOf(blockId)!.fam.kind;
    const bonusItem = k === 'wheat' || k === 'beetroots' ? drops[1] : drops[0];
    if (bonusItem) for (let i = 0; i < lvl; i++) if (rnd() < 0.5714) bonusItem.count++;
    return drops;
  }
  if (isOreDrop(blockId)) {
    for (const s of drops) {
      if (s.id === blockId) continue;
      const k = Math.floor(rnd() * (lvl + 2)) - 1;
      if (k > 0) s.count *= k + 1;
    }
  } else if (/Redstone Ore|Lapis|Copper Ore|Nether Gold Ore|Glowstone|Melon/.test(n)) {
    const cap = /Glowstone/.test(n) ? 4 : /Melon/.test(n) ? 9 : 64;
    for (const s of drops) s.count = Math.min(cap, s.count + Math.floor(rnd() * (lvl + 1)));
  }
  return drops;
}
